import type {
  AccountingState,
  bakery,
  DeliveryJobStatus,
  DocumentState,
  EndpointResponse,
  ItemAvailability,
  internal,
  OrderPhase,
  OrderSummaryState,
  PayoutVerificationState,
  partner,
} from '@croissants/contracts';
import type {
  ApartmentState,
  BakeryServiceStatus,
  GuestException,
  OrganizationStanding,
  SupportReasonKind,
  TimingVerdict,
} from '@croissants/contracts/projections';
import type { GuestStageId, MessageKey } from '@croissants/i18n';
import type { BadgeTone } from './primitives';

/**
 * Every status pill on every portal, in one list (#2004).
 *
 * A pill shows a STATE, in a word the founder approved once for every portal
 * (docs/plans/pill-wording-approval.html, revision 3, approved in full on
 * 2026-10-08), in one colour. Until this list existed each of the 86 places
 * that drew one passed its own word and its own colour, and nine screens kept
 * a private word-to-colour map: that is how "Pending" came to have ten
 * meanings and three colours, and how one baker's pause read red on Internal
 * and amber on Partner.
 *
 * So a screen names the FAMILY and the STATE and nothing else. The colour is
 * this list's, the word is `pill.<family>.<state>` in the platform catalogue
 * (`@croissants/i18n`) - or the one key `PILL_WORD_KEYS` points it at, when
 * several states read as one word - and each family is keyed by the contract's
 * own enum: a state added to the wire without a word and a colour here is a
 * typecheck failure, not a grey pill with a raw code in it.
 *
 * THE COLOUR RULE (approved): green - done, or good to go; blue - moving
 * along, nothing to do; amber - waiting for somebody to act; red - failed,
 * blocked, or not taking orders; grey - over, or not in play (a calendar
 * closure included). Grey is the platform's true grey status hue (`grey`),
 * never the warm `neutral` badge - the founder's 13 September call for a
 * closed bakery, and the grey the approval page drew every such word in. A
 * word has ONE colour wherever it appears; "Open" is the one word with two, on
 * purpose (`TWO_COLOURS_ALLOWED`).
 *
 * THE REASON beside a state - why a bakery is paused, what an apartment lacks
 * - is ordinary text next to the pill, never inside it and never a pill of its
 * own: `PILL_REASONS` below.
 */
export const PILL_FAMILIES = {
  /** A payout: Internal's approval state and Partner's payout status, the same four states. */
  payout: {
    PENDING: 'info',
    PAID: 'success',
    FAILED: 'danger',
    CANCELED: 'grey',
  } satisfies Record<internal.PayoutApprovalState | partner.PartnerPayout['status'], BadgeTone>,

  /*
   * THE ORDER. Internal Ops reads seven words - Payment pending, Awaiting
   * bakery, Preparing, Ready, In transit, Delivered, Canceled - and the guest
   * seven of its own (`guestStage`). Whether an operator must act is NOT the
   * pill's colour any more: a word has one colour, and "Needs an operator" is
   * a red line of text under the state (founder, 2026-10-08).
   */

  /**
   * The order's one summary state: the Admin dashboard's, the Orders list's
   * and the order record's pill. Several states read as one word
   * (`PILL_WORD_KEYS`): a placed order is awaiting its bakery, a courier search
   * is a Ready bag (the courier column says Searching), and an order that
   * found no bakery or whose card was refused is Canceled - why is the reason
   * beside it. DRAFT and NEEDS_OPERATOR are never produced (P3, P17) and keep
   * an entry only because the wire's enum names them: DRAFT reads the word of
   * the state it would become, Payment pending, and NEEDS_OPERATOR the open
   * order's Awaiting bakery.
   */
  orderSummary: {
    DRAFT: 'info',
    PAYMENT_PENDING: 'info',
    PLACED: 'info',
    AWAITING_BAKERY: 'info',
    PREPARING: 'info',
    READY: 'info',
    FINDING_COURIER: 'info',
    ON_THE_WAY: 'info',
    DELIVERED: 'success',
    CANCELLED: 'grey',
    UNALLOCATED: 'grey',
    COURIER_UNAVAILABLE: 'info',
    PAYMENT_FAILED: 'grey',
    NEEDS_OPERATOR: 'info',
  } satisfies Record<OrderSummaryState, BadgeTone>,

  /**
   * Where the order's own region stands: the Orders list's State column and
   * its filter. The same seven words: every phase before the bag is started is
   * Awaiting bakery - a CREATED order whose payment is not confirmed yet draws
   * `orderSummary`'s Payment pending instead (P129) - a collected bag is In
   * transit, and a closed order - bookkeeping after delivery - Delivered. A
   * refusal waiting for the engine (#2155) is Awaiting bakery too: the
   * position itself is shown nowhere (decision 3).
   */
  orderLifecycle: {
    CREATED: 'info',
    SCHEDULED: 'info',
    ACCEPTANCE_OPEN: 'info',
    ACCEPTANCE_CALLING: 'info',
    PREPARING: 'info',
    READY: 'info',
    AWAITING_ASSIGNMENT: 'info',
    COLLECTED: 'info',
    DELIVERED: 'success',
    CANCELLED: 'grey',
    CLOSED: 'success',
  } satisfies Record<OrderPhase, BadgeTone>,

  /**
   * The courier job, as the provider reports it: the Admin dashboard's delivery
   * column. PENDING and QUOTED are never shown (P18, P19) and read the job's
   * first word, Searching. A courier who has the bag is In transit, the
   * order's own word for the same moment.
   */
  courierJob: {
    PENDING: 'info',
    QUOTED: 'info',
    CREATED: 'info',
    COURIER_ASSIGNED: 'info',
    PICKED_UP: 'info',
    ARRIVING: 'info',
    RETURNING: 'warning',
    DELIVERED: 'success',
    CANCELLED: 'grey',
    FAILED: 'danger',
  } satisfies Record<DeliveryJobStatus, BadgeTone>,

  /**
   * How a live order stands against its promised window, in four bands (the
   * Admin dashboard's risk column, RTE-006). Derived on the client, so this
   * family IS the list of bands: `LatenessSeverity` in Internal's
   * `lib/lateness.ts` is `PillState<'latenessBand'>` plus CLOSED - an order
   * that stopped, whose cell is empty because the status beside it already
   * says Delivered or Canceled (P32).
   */
  latenessBand: {
    LATE: 'danger',
    AT_RISK: 'warning',
    WATCH: 'info',
    ON_TRACK: 'success',
  },

  /** A milestone of the order against the deadline that governed it (the order record's timing table). */
  timingVerdict: {
    ON_TIME: 'success',
    EARLY: 'warning',
    LATE: 'danger',
    MISSED: 'grey',
    PENDING: 'info',
  } satisfies Record<TimingVerdict, BadgeTone>,

  /**
   * Flags that are not a record's state: both ordering modes off on the Admin
   * dashboard (OPS-025 - selling nothing anywhere is not left to be inferred
   * from an empty board), and an order wanted now.
   */
  tag: {
    ORDERING_CLOSED: 'danger',
    NOW: 'warning',
    /** A shop whose counter is the platform's own robot (Internal's Active bakeries). */
    ROBOBAKER: 'info',
    /** A bakery on the coverage map with no apartment it can reach (Internal's Map). */
    SERVES_NOWHERE: 'grey',
    /** A payout or a document a simulation run produced (Documents, an organization's payouts). */
    SIMULATION: 'grey',
    /** Partner's sidebar, signed in through the local development bypass. */
    DEV_SESSION: 'warning',
    /** An order that is one morning of a longer stay (Partner's Orders): Internal's word, Stay. */
    MULTI_DAY_STAY: 'grey',
  },

  /*
   * THE COUNTERS AND THE SERVICE.
   */

  /**
   * A shop's one bakery status on Active bakeries and Health (OPS-035), the
   * server's `bakeryServiceStatusOf`. Red for anything that stops this bakery
   * taking orders; Closed is grey, the calendar's rather than a problem
   * (founder, 2026-09-13), and a recorded closure is a kind of Closed (with
   * its end beside it); Not ready is amber, a row an operator completes. A
   * pause is a pause whoever made it - the baker, the kill switch, or two
   * orders that went unanswered - and a house whose payout setup or VAT is
   * unfinished is Suspended, as its standing already says (P261).
   */
  bakeryService: {
    OPEN: 'success',
    UNREACHABLE: 'danger',
    CLOSED: 'grey',
    VACATION: 'grey',
    PAUSED: 'danger',
    IGNORING_ORDERS: 'danger',
    INCOMPLETE: 'warning',
    SUSPENDED: 'danger',
  } satisfies Record<BakeryServiceStatus, BadgeTone>,

  /**
   * One paired device under its shop on Active bakeries: can it be sent an
   * order now. Online when it can - in front, or in the background with its
   * alerts still ringing - and Offline when it cannot. INACTIVE is never
   * produced any more (P63) and reads Offline: an inactive record cannot be
   * sent an order either.
   */
  deviceConnectivity: {
    READY: 'success',
    STANDBY: 'success',
    UNREACHABLE: 'danger',
    INACTIVE: 'danger',
  } satisfies Record<internal.BakeryReadinessDevice['connectivity'], BadgeTone>,

  /**
   * A handheld in the fleet (OPS-029): the Devices list and a device's page.
   * Four words: Online (it can be sent orders and rings - a closed app in the
   * background included), Offline (it cannot - a handheld probably asleep on
   * its charger included), Blocked (below the minimum version after the
   * force-update deadline) and Updating. REMOVED is never produced (P80) and
   * reads Offline.
   */
  device: {
    ONLINE: 'success',
    UPDATING: 'info',
    CLOSED: 'success',
    ASLEEP: 'danger',
    OFFLINE: 'danger',
    BLOCKED: 'danger',
    REMOVED: 'danger',
  } satisfies Record<internal.FleetDeviceStatus, BadgeTone>,

  /**
   * A device row in the Devices dialog both portals share (`DevicesDialog`):
   * the Devices list's words for the same facts (P78, P84) - Online when the
   * unit can be sent an order, with "In background" beside it when no app
   * screen is in front (it still rings), and Offline when it cannot. Judged
   * on the cadence of the unit's own last beat, as Devices is.
   */
  pairedDevice: {
    ONLINE: 'success',
    BACKGROUND: 'success',
    UNREACHABLE: 'danger',
  },

  /**
   * The ONE tag a baker's house, a partner or a shop carries
   * (docs/finance/onboarding.html#suspension): an organization's two standings,
   * and a shop's third - Not ready, amber: nobody suspended it, there is a row
   * to complete (the Active bakeries table's word for the same fact, P260).
   */
  standing: {
    ACTIVE: 'success',
    SUSPENDED: 'danger',
    INCOMPLETE: 'warning',
  } satisfies Record<OrganizationStanding | internal.BakeryLocationStateFilter, BadgeTone>,

  /**
   * Health status: a pre-service condition (OK or Blocked) and the delivery
   * provider's verdict (RTE-022), in one vocabulary. "Ready" is no longer one
   * of them: Ready now means only that a bag is ready (P33).
   */
  healthVerdict: {
    GREEN: 'success',
    AMBER: 'warning',
    RED: 'danger',
  },

  /** Whether the accounting export (Pennylane) is configured, on Health status. */
  accountingExport: {
    ON: 'success',
    OFF: 'grey',
  },

  /**
   * How serious an Activity feed item is (OPS-027). INFO is never drawn as a
   * pill - the feed drops it - but names the filter's option.
   */
  alertSeverity: {
    DANGER: 'danger',
    WARNING: 'warning',
    INFO: 'grey',
  } satisfies Record<internal.ActivitySeverity, BadgeTone>,

  /**
   * A dead-lettered event nobody has replayed yet, amber: a case waiting for
   * an operator. A replayed one shows when, as a date.
   */
  deadLetter: {
    OPEN: 'warning',
  },

  /**
   * A handheld build's rollout (Releases), halted: published and offered to
   * nobody. Its Signed and Published columns are a tick or a dash, not pills
   * (P153).
   */
  releaseStep: {
    HALTED: 'danger',
  },

  /** A scenario run's verdict (OPS-038). */
  scenarioRun: {
    PASS: 'success',
    FAIL: 'danger',
    INCONCLUSIVE: 'warning',
  } satisfies Record<
    EndpointResponse<typeof internal.listScenarioRuns>['items'][number]['verdict'],
    BadgeTone
  >,

  /*
   * THE MONEY, on Internal Ops and Partner.
   */

  /**
   * Where an organization's Stripe balance stands for its next payout
   * (docs/finance/payments-and-payouts.html#approval): Pending payouts and a
   * payout's preview. Three words: Payable (it can be paid now, waiting for
   * an operator to approve), Blocked (it cannot - why is beside it: a negative
   * balance, funds held, bank details or an identity check missing, Stripe
   * restricting it) and Pending (money moving, nothing to do).
   */
  payableAccount: {
    READY: 'warning',
    NEGATIVE: 'danger',
    HELD: 'danger',
    BANK_DETAILS_NEEDED: 'danger',
    ACCOUNT_RESTRICTED: 'danger',
    IN_TRANSIT: 'info',
    IDENTITY_INCOMPLETE: 'danger',
    FUNDS_PENDING: 'info',
  } satisfies Record<internal.PayableAccountState, BadgeTone>,

  /**
   * A Croissant cycle's payout to Qonto: its row on Pending payouts and its
   * approval. Payable and Pending as a balance reads them (`payableAccount`).
   */
  monthlyPayout: {
    READY: 'warning',
    FUNDS_PENDING: 'info',
    NOTHING: 'grey',
    IN_TRANSIT: 'info',
    PAID: 'success',
    FAILED: 'danger',
  } satisfies Record<internal.MonthlyPayoutState, BadgeTone>,

  /**
   * A Croissant cycle's preview, on its page: the cycle's own words and
   * colours, and In progress for a month still running - "Open" now means
   * only a shop taking orders or a case waiting (P111).
   */
  monthlyPreview: {
    READY: 'warning',
    FUNDS_PENDING: 'info',
    NOTHING: 'grey',
    NOT_CLOSED: 'info',
    IN_TRANSIT: 'info',
    PAID: 'success',
  } satisfies Record<internal.MonthlyPayoutPreview['state'], BadgeTone>,

  /** A movement of a payout whose money Stripe has not made available yet (a payout's page). */
  payoutMovement: {
    PENDING: 'info',
  },

  /**
   * A money figure that is not final yet: a Stripe fee estimated where Stripe
   * stated none or a courier cost still expected (Estimated, P91, P262), and
   * a payout share not paid out yet (Pending, P95).
   */
  figure: {
    ESTIMATED: 'info',
    PENDING: 'info',
  },

  /** The latest reconciliation run, on Finance's ledger card. */
  reconciliation: {
    CLEAN: 'success',
    DRIFT: 'danger',
  },

  /**
   * A delivery provider's statement (docs/finance/reconciliation.html#delivery-statement),
   * derived on the client from its cycle and its invoice match, so this family
   * IS the list: `DeliveryStatementStanding` in Internal's DeliveryStatements
   * is `PillState<'deliveryStatement'>`. A cycle still running is In
   * progress, as a month is.
   */
  deliveryStatement: {
    OPEN: 'info',
    UNMATCHED: 'warning',
    MATCHED: 'success',
    DIFFERS: 'danger',
  },

  /**
   * A document's export to the accounting software (Pennylane), on Documents.
   * A document that is never exported leaves the cell empty: its type already
   * says so (P119).
   */
  documentExport: {
    PENDING: 'info',
    EXPORTED: 'success',
    FAILED: 'danger',
  } satisfies Record<Exclude<AccountingState, 'NOT_APPLICABLE'>, BadgeTone>,

  /**
   * An organization's payout account at Stripe, as an operator reads it: the
   * Organizations list, a client's and a bakery's page. Incomplete (the owner
   * has not finished the Stripe form) is amber: nothing the house sells can be
   * paid to it until they do.
   */
  payoutAccount: {
    NOT_STARTED: 'warning',
    PENDING: 'info',
    VERIFIED: 'success',
    RESTRICTED: 'danger',
  } satisfies Record<PayoutVerificationState, BadgeTone>,

  /**
   * A document (an invoice, a credit note, a statement): Internal's Documents,
   * and on Partner the one word beside a document a credit note canceled.
   * DRAFT is never listed (P116) and reads Pending, the nearest approved word
   * for a document not issued yet.
   */
  document: {
    DRAFT: 'info',
    ISSUED: 'success',
    CANCELLED: 'grey',
  } satisfies Record<DocumentState, BadgeTone>,

  /**
   * The same Stripe account as `payoutAccount`, as its OWNER reads it on
   * Partner's Settings - derived on the client (`payoutBadgeOf`), so this
   * family IS the list. Until the owner has sent Stripe everything the account
   * is Incomplete, whatever the provider calls it.
   */
  payoutOnboarding: {
    INCOMPLETE: 'warning',
    PENDING: 'info',
    VERIFIED: 'success',
    RESTRICTED: 'danger',
  },

  /**
   * A conciergerie's commission on Partner's Orders: Pending until the payout
   * carrying it has reached the bank, then Paid - the Payouts screen's own two
   * words for the same money (#2004, approved 2026-10-08). NONE (no
   * commission) draws no pill.
   */
  commission: {
    PENDING: 'info',
    PAID: 'success',
  } satisfies Record<Exclude<partner.PartnerOrderRow['commissionState'], 'NONE'>, BadgeTone>,

  /*
   * PARTNER, THE GUEST AND INTERNAL'S CLIENT AND SET-MENU SCREENS.
   */

  /**
   * Partner's QR allowance (WEB-014), on Settings: how many codes the owner
   * may still mint, or the allowance spent.
   */
  qrAllowance: {
    REMAINING: 'info',
    EXHAUSTED: 'warning',
  },

  /**
   * The ONE word an apartment row states (CON-001), from `apartmentState` in
   * `@croissants/contracts/projections`: Partner's portfolio, an apartment's
   * page. Active is the resting state; an apartment with no code yet or that
   * no bakery reaches is Not ready, amber, with what is missing beside it; a
   * pause stops orders and is red, as every pause is; archived is terminal
   * and therefore quiet.
   */
  apartment: {
    ARCHIVED: 'grey',
    SUSPENDED: 'danger',
    NOT_SERVABLE: 'warning',
    QR_MISSING: 'warning',
    PAUSED: 'danger',
    LIVE: 'success',
  } satisfies Record<ApartmentState, BadgeTone>,

  /**
   * An apartment on Internal's client record: `apartment`'s words and
   * colours (`PILL_WORD_KEYS` points every state at them), a family of its own
   * only because Internal's reason beside Not ready is worded for an
   * operator (`PILL_REASONS`).
   */
  clientApartment: {
    ARCHIVED: 'grey',
    SUSPENDED: 'danger',
    NOT_SERVABLE: 'warning',
    QR_MISSING: 'warning',
    PAUSED: 'danger',
    LIVE: 'success',
  } satisfies Record<ApartmentState, BadgeTone>,

  /**
   * One apartment's fate in a Partner bulk run (CON-001): Done, Skipped or
   * Failed, whatever the action, and why beside it (`PILL_REASONS`). Derived
   * on the client (`lib/bulk-report.ts`), so this family IS the list.
   */
  bulkResult: {
    DONE: 'success',
    SKIPPED: 'grey',
    FAILED: 'danger',
  },

  /**
   * A shop, as its own house reads it: Partner's Shops (BAK-028). Suspension
   * and the BAK-006 service pause are two facts, so a paused-but-active shop
   * wears ACTIVE and PAUSED side by side. Suspended - not allowed to trade -
   * is red, as the house's standing is.
   */
  shop: {
    ACTIVE: 'success',
    SUSPENDED: 'danger',
    PAUSED: 'danger',
  },

  /** A product on a house's own shelf (Partner's Products): on sale, or taken off sale. */
  product: {
    ACTIVE: 'success',
    RETIRED: 'grey',
  },

  /**
   * A reason on a Support case (Internal's Support, issue #2272): what opened
   * the case or joined it, one word each. Who added it and when is text beside.
   */
  supportReason: {
    REMOVED: 'info',
    LOST: 'danger',
    CANCELLED: 'grey',
    COMPLAINT: 'warning',
    DISPUTE: 'danger',
    FRAUD: 'danger',
    STRIPE: 'info',
    OTHER: 'grey',
    WASTED_RIDER: 'warning',
    REFUND_REFUSED: 'danger',
  } satisfies Record<SupportReasonKind, BadgeTone>,

  /**
   * One instruction the platform gave Stripe (Internal's Support, Payments):
   * Failed is retried on its own and reads Retrying; Needs human is Failed.
   */
  paymentMovement: {
    PENDING: 'info',
    SENT: 'info',
    CONFIRMED: 'success',
    FAILED: 'warning',
    NEEDS_HUMAN: 'danger',
  } satisfies Record<
    EndpointResponse<typeof internal.listPaymentMovements>['items'][number]['status'],
    BadgeTone
  >,

  /**
   * A listed product the counter has none of: beside it on Partner's
   * Products, and on the guest's storefront tile. One word, Sold out; "until
   * further notice" is the reason beside it. IN_STOCK draws no pill.
   */
  availability: {
    SOLD_OUT_TODAY: 'warning',
    HIDDEN: 'warning',
  } satisfies Record<Exclude<ItemAvailability, 'IN_STOCK'>, BadgeTone>,

  /** A member of a house's team (Partner's Team). */
  teamMember: {
    INVITED: 'info',
    ACTIVE: 'success',
    DISABLED: 'grey',
  } satisfies Record<partner.PartnerMember['status'], BadgeTone>,

  /**
   * A house's row in a set menu's prices (Internal's set-menu editor): items
   * it has no price for, and a commission it cannot earn - Negative, on every
   * pick or on some (the reason beside it).
   */
  formuleHouse: {
    UNPRICED: 'warning',
    NEGATIVE: 'danger',
    NEGATIVE_SOME: 'danger',
  },

  /**
   * Where a guest's order is, on the guest's orders list: the approved pill
   * words, Placed, Confirmed, Preparing, Ready, In transit, Delivered. They
   * are NOT the tracker's stage titles (`stage.*`, the PRD's own words,
   * GST-036): those stay verbatim on the order page and in the messages, and
   * the pill reads the shorter word. A ready bag, a courier being found and a
   * courier on the way to it are all Ready; riding and arriving are In
   * transit.
   */
  guestStage: {
    ORDER_PLACED: 'info',
    ORDER_CONFIRMED: 'info',
    PREPARING: 'info',
    ORDER_READY: 'info',
    FINDING_COURIER: 'info',
    COURIER_ASSIGNED: 'info',
    ON_THE_WAY: 'info',
    ARRIVING: 'info',
    DELIVERED: 'success',
  } satisfies Record<GuestStageId, BadgeTone>,

  /**
   * A booked order nobody has started, on the guest's orders list: how far off
   * its morning is (founder, 2026-08-24, kept 2026-10-06), or - on the morning
   * itself, once a bakery has taken it - that it is in hand.
   */
  guestOrderDue: {
    TODAY: 'info',
    IN_PROGRESS: 'info',
    TOMORROW: 'info',
    IN_DAYS: 'info',
  },

  /**
   * How a guest's order went wrong (GST-036), on the guest's orders list and
   * order page: Canceled, whatever went wrong - why is the message beside it
   * (`PILL_REASONS`), shown until the guest dismisses it.
   */
  guestException: {
    PAYMENT_FAILED: 'grey',
    CANCELLED: 'grey',
    UNALLOCATED: 'grey',
    DELIVERY_FAILED: 'grey',
    DELIVERY_RETURNING: 'grey',
  } satisfies Record<GuestException, BadgeTone>,

  /**
   * What a counter's stock-out did to a guest's order (BAK-019), with its
   * cause inside the pill (founder, 2026-10-06): the count on the order's row,
   * and on a line of the order page what was removed or that it was replaced
   * (the product it replaced is named on the line, not in the pill).
   */
  stockOut: {
    ORDER_ITEMS: 'warning',
    LINE_REMOVED: 'warning',
    UNITS_REMOVED: 'warning',
    REPLACED: 'warning',
  },

  /*
   * THE SUNMI HANDHELD. The counter is a Java app outside the pnpm graph, so
   * it cannot read this list: it keeps its own copy - the word in
   * res/values/strings.xml (`history_outcome_<CODE>`), the line under it
   * (`history_reason_<CODE>`) and the colour in History.java `colourId()`.
   * `scripts/check-counter-pills.mjs` holds all three to THIS family, word
   * for word, line for line and colour for colour, so the copy cannot drift.
   */

  /**
   * How an order ended for THIS counter, on the handheld's History tile and
   * its order view (BAK-062; #2004, approved 2026-10-08): four words and no
   * other - En route (the rider has the bag, blue), Livree (green), Annulee
   * (grey) and Refusee (this counter handed it back, red) - with the line
   * under Annulee and Refusee in `PILL_REASONS`. The first three are the
   * ORDER's own words (`PILL_WORD_KEYS`), so a baker and an operator read one
   * word for one ending. UNKNOWN is the handheld's fallback for a code a build
   * has no word for (`history_outcome_unknown`, "Terminee"): the platform
   * never sends one, and it stays so an older build meeting a newer code
   * still prints a word. The handheld is French-only; the English words say
   * what each means.
   */
  counterOutcome: {
    ON_THE_WAY: 'info',
    DELIVERED: 'success',
    CANCELLED: 'grey',
    REFUSED: 'danger',
    UNKNOWN: 'grey',
  } satisfies Record<bakery.BakeryOrderOutcome | 'UNKNOWN', BadgeTone>,
} as const;

export type PillFamily = keyof typeof PILL_FAMILIES;
export type PillState<F extends PillFamily> = keyof (typeof PILL_FAMILIES)[F] & string;

/**
 * Where several states read as ONE word, the one key that word is written
 * under (#2004): every other state's word is `pill.<family>.<state>`.
 *
 * The approved wording merged many states into one word - a placed order is
 * Awaiting bakery as one offered to a counter is, a recorded closure is
 * Closed, five reasons a balance cannot be paid are all Blocked - and a word
 * written twice is a word that can drift apart. So the merged states name the
 * key of the state they merged into, and only that key holds the word.
 * `status-pill.test.tsx` pins that each points at a real key in both
 * catalogues.
 */
export const PILL_WORD_KEYS = {
  pairedDevice: {
    BACKGROUND: 'pill.pairedDevice.ONLINE',
  },
  orderSummary: {
    DRAFT: 'pill.orderSummary.PAYMENT_PENDING',
    PLACED: 'pill.orderSummary.AWAITING_BAKERY',
    FINDING_COURIER: 'pill.orderSummary.READY',
    COURIER_UNAVAILABLE: 'pill.orderSummary.READY',
    UNALLOCATED: 'pill.orderSummary.CANCELLED',
    PAYMENT_FAILED: 'pill.orderSummary.CANCELLED',
    NEEDS_OPERATOR: 'pill.orderSummary.AWAITING_BAKERY',
  },
  orderLifecycle: {
    CREATED: 'pill.orderSummary.AWAITING_BAKERY',
    SCHEDULED: 'pill.orderSummary.AWAITING_BAKERY',
    ACCEPTANCE_OPEN: 'pill.orderSummary.AWAITING_BAKERY',
    ACCEPTANCE_CALLING: 'pill.orderSummary.AWAITING_BAKERY',
    PREPARING: 'pill.orderSummary.PREPARING',
    READY: 'pill.orderSummary.READY',
    AWAITING_ASSIGNMENT: 'pill.orderSummary.AWAITING_BAKERY',
    COLLECTED: 'pill.orderSummary.ON_THE_WAY',
    DELIVERED: 'pill.orderSummary.DELIVERED',
    CANCELLED: 'pill.orderSummary.CANCELLED',
    CLOSED: 'pill.orderSummary.DELIVERED',
  },
  courierJob: {
    PENDING: 'pill.courierJob.CREATED',
    QUOTED: 'pill.courierJob.CREATED',
  },
  bakeryService: {
    VACATION: 'pill.bakeryService.CLOSED',
    IGNORING_ORDERS: 'pill.bakeryService.PAUSED',
  },
  deviceConnectivity: {
    STANDBY: 'pill.deviceConnectivity.READY',
    INACTIVE: 'pill.deviceConnectivity.UNREACHABLE',
  },
  device: {
    CLOSED: 'pill.device.ONLINE',
    ASLEEP: 'pill.device.OFFLINE',
    REMOVED: 'pill.device.OFFLINE',
  },
  payableAccount: {
    NEGATIVE: 'pill.payableAccount.BLOCKED',
    HELD: 'pill.payableAccount.BLOCKED',
    BANK_DETAILS_NEEDED: 'pill.payableAccount.BLOCKED',
    ACCOUNT_RESTRICTED: 'pill.payableAccount.BLOCKED',
    IDENTITY_INCOMPLETE: 'pill.payableAccount.BLOCKED',
    IN_TRANSIT: 'pill.payableAccount.FUNDS_PENDING',
  },
  monthlyPayout: {
    IN_TRANSIT: 'pill.monthlyPayout.FUNDS_PENDING',
  },
  monthlyPreview: {
    IN_TRANSIT: 'pill.monthlyPreview.FUNDS_PENDING',
  },
  document: {
    DRAFT: 'pill.payout.PENDING',
  },
  clientApartment: {
    ARCHIVED: 'pill.apartment.ARCHIVED',
    SUSPENDED: 'pill.shop.SUSPENDED',
    NOT_SERVABLE: 'pill.apartment.NOT_SERVABLE',
    QR_MISSING: 'pill.apartment.NOT_SERVABLE',
    PAUSED: 'pill.apartment.PAUSED',
    LIVE: 'pill.apartment.LIVE',
  },
  apartment: {
    SUSPENDED: 'pill.shop.SUSPENDED',
    QR_MISSING: 'pill.apartment.NOT_SERVABLE',
  },
  availability: {
    HIDDEN: 'pill.availability.SOLD_OUT_TODAY',
  },
  formuleHouse: {
    NEGATIVE_SOME: 'pill.formuleHouse.NEGATIVE',
  },
  guestStage: {
    FINDING_COURIER: 'pill.guestStage.ORDER_READY',
    COURIER_ASSIGNED: 'pill.guestStage.ORDER_READY',
    ARRIVING: 'pill.guestStage.ON_THE_WAY',
  },
  guestException: {
    PAYMENT_FAILED: 'pill.guestException.CANCELLED',
    UNALLOCATED: 'pill.guestException.CANCELLED',
    DELIVERY_FAILED: 'pill.guestException.CANCELLED',
    DELIVERY_RETURNING: 'pill.guestException.CANCELLED',
  },
  counterOutcome: {
    ON_THE_WAY: 'pill.orderSummary.ON_THE_WAY',
    DELIVERED: 'pill.orderSummary.DELIVERED',
    CANCELLED: 'pill.orderSummary.CANCELLED',
  },
} as const satisfies {
  readonly [F in PillFamily]?: { readonly [S in PillState<F>]?: MessageKey };
};

/**
 * THE REASON BESIDE A STATE (#2004, approved 2026-10-08): why a bakery is
 * paused, what an apartment lacks, why a balance cannot be paid.
 *
 * A pill says the state; the reason is ordinary text next to it, never
 * inside it and never a pill of its own. Each state lists the reasons it can
 * carry, and a reason's words are `pill.<family>.reason.<REASON>` in the
 * catalogue. A state with ONE reason is the common case - a merged state
 * whose old word becomes its reason (Vacation is Closed with "Closure through
 * {date}") - and `StatusPill withReason` prints it; a state whose reason
 * depends on the record (a pause by the baker or the kill switch) is given it
 * by name (`reason=`). Internal Ops always shows the reason.
 *
 * The ORDER's reasons are here too, as the approval's section 3 words them:
 * why it was canceled is the cause the order RECORDS (`cancelledCause`, one
 * reason per `OrderCancelledCause`, a refused card included), a Ready order
 * whose courier search is still running says until when, and the Orders
 * list's merged states keep their old word beside the new one (Awaiting
 * bakery: Scheduled, Open to answer, Ringing at the counter; Delivered:
 * Closed). `orderSummaryReasonOf` (status-pill.tsx) is the one place a
 * screen asks which.
 *
 * The GUEST reads the same causes as a message of their own (section 3): the
 * `guestException` reasons, one per `GuestEnding` (`guestEndingOf`,
 * `@croissants/contracts/projections`), shown on the orders list and the order
 * page until the guest dismisses it - and nothing when the guest cancelled. A
 * stay morning carries the same sentences beside its Canceled, plus the two
 * only a morning has: the guest skipped it, or no bakery served it.
 */
export const PILL_REASONS = {
  orderSummary: {
    CANCELLED: [
      'CASCADE_EXHAUSTED',
      'NO_ELIGIBLE_HOUSE',
      'WINDOW_SWEEP',
      'COUNTER_GAVE_BACK',
      'NEVER_PREPARED',
      'CLOSURE',
      'GUEST',
      'OPERATOR',
      'NO_COURIER',
      'NEVER_COLLECTED',
      'DELIVERY_FAILED',
      'PROPERTY_REMOVED',
      'NEVER_PAID',
    ],
    UNALLOCATED: ['CASCADE_EXHAUSTED', 'NO_ELIGIBLE_HOUSE'],
    PAYMENT_FAILED: ['NEVER_PAID'],
    COURIER_UNAVAILABLE: ['SEARCHING_UNTIL', 'NO_COURIER_YET'],
  },
  orderLifecycle: {
    SCHEDULED: ['SCHEDULED'],
    ACCEPTANCE_OPEN: ['OPEN_TO_ANSWER'],
    ACCEPTANCE_CALLING: ['RINGING'],
    CLOSED: ['CLOSED'],
  },
  bakeryService: {
    UNREACHABLE: ['NO_DEVICE_ANSWERING', 'NO_DEVICE_PAIRED'],
    VACATION: ['CLOSURE_THROUGH'],
    PAUSED: ['BAKER', 'KILL_SWITCH'],
    IGNORING_ORDERS: ['TWO_UNANSWERED'],
    INCOMPLETE: ['PICKUP_DETAILS'],
    SUSPENDED: ['PAYOUT_SETUP', 'VAT'],
  },
  deviceConnectivity: {
    STANDBY: ['IN_BACKGROUND'],
  },
  pairedDevice: {
    BACKGROUND: ['IN_BACKGROUND'],
  },
  device: {
    CLOSED: ['IN_BACKGROUND'],
    ASLEEP: ['CHARGING'],
    BLOCKED: ['UPDATE_REQUIRED'],
  },
  payableAccount: {
    NEGATIVE: ['NEGATIVE_BALANCE'],
    HELD: ['FUNDS_HELD'],
    BANK_DETAILS_NEEDED: ['BANK_DETAILS'],
    ACCOUNT_RESTRICTED: ['RESTRICTED'],
    IDENTITY_INCOMPLETE: ['IDENTITY'],
    IN_TRANSIT: ['ON_ITS_WAY'],
    FUNDS_PENDING: ['FUNDS_NOT_AVAILABLE'],
  },
  apartment: {
    NOT_SERVABLE: ['NO_BAKERY'],
    QR_MISSING: ['NO_QR'],
  },
  clientApartment: {
    NOT_SERVABLE: ['NO_BAKERY'],
    QR_MISSING: ['NO_QR'],
  },
  bulkResult: {
    SKIPPED: ['ALREADY_ARCHIVED', 'WAS_NOT_ACTIVE', 'WAS_NOT_PAUSED', 'ARCHIVED'],
    FAILED: [
      'ARCHIVE_FAILED',
      'STILL_TAKING_ORDERS',
      'STILL_PAUSED',
      'PRINT_FAILED',
      'ALLOWANCE_USED',
    ],
  },
  availability: {
    HIDDEN: ['UNTIL_FURTHER_NOTICE'],
  },
  guestException: {
    PAYMENT_FAILED: ['CARD_REFUSED'],
    CANCELLED: [
      'NO_BAKERY',
      'BAKERY_COULD_NOT_PREPARE',
      'BAKERY_CLOSED',
      'OPERATOR',
      'NO_COURIER',
      'NEVER_COLLECTED',
      'COURIER_COULD_NOT_DELIVER',
      'APARTMENT_REMOVED',
      'CARD_REFUSED',
    ],
    UNALLOCATED: ['NO_BAKERY'],
    DELIVERY_FAILED: ['COURIER_COULD_NOT_DELIVER'],
    DELIVERY_RETURNING: ['DELIVERY_RETURNING'],
  },
  formuleHouse: {
    NEGATIVE: ['EVERY_PICK'],
    NEGATIVE_SOME: ['SOME_PICKS'],
  },
  /*
   * The Sunmi's line under its word (#2004, approved 2026-10-08): the cause
   * under Annulee, or that an operator moved the order to another bakery; who
   * refused it under Refusee. One per `BakeryOutcomeReason` - a reason added to
   * the wire without its line here is a typecheck failure (status-pill.tsx).
   */
  counterOutcome: {
    CANCELLED: [
      'NO_BAKERY',
      'GIVEN_BACK',
      'NEVER_PREPARED',
      'CLOSURE',
      'GUEST',
      'OPERATOR',
      'REASSIGNED',
      'NO_COURIER',
      'NEVER_COLLECTED',
      'DELIVERY_FAILED',
      'PROPERTY_REMOVED',
    ],
    REFUSED: ['BY_YOU', 'NO_ANSWER'],
  },
} as const satisfies {
  readonly [F in PillFamily]?: { readonly [S in PillState<F>]?: readonly string[] };
};

/** The reasons a state of a family can carry; `never` for a state that carries none. */
export type PillReason<
  F extends PillFamily,
  S extends PillState<F>,
> = F extends keyof typeof PILL_REASONS
  ? S extends keyof (typeof PILL_REASONS)[F]
    ? (typeof PILL_REASONS)[F][S] extends readonly (infer R)[]
      ? R & string
      : never
    : never
  : never;

/*
 * ---------------------------------------------------------------------------
 * THE TWO ALLOW LISTS
 * ---------------------------------------------------------------------------
 *
 * The founder's rules for a pill are that a word has ONE colour wherever it
 * appears and that a pill is a word or two, not a sentence
 * (status-pill.test.tsx pins both). The words that may break one, each group
 * with its reason, are written here as data so docs/pill-list.html can print
 * each reason beside its words (`scripts/build-pill-list.mjs`). These lists
 * only shrink.
 */

/** Words that break one of a pill's rules, and why. */
export interface PillAllowance {
  /** The words exactly as a catalogue holds them, placeholders included. */
  readonly words: readonly string[];
  readonly reason: string;
}

/**
 * Words that may wear two colours. The approved wording keeps exactly one:
 * "Open" is green for a bakery taking orders and amber for a case waiting for
 * an operator.
 */
export const TWO_COLOURS_ALLOWED: readonly PillAllowance[] = [
  {
    words: ['Open'],
    reason:
      'Approved on purpose: a bakery taking orders (bakeryService, green) and a dead letter waiting for an operator (deadLetter, amber).',
  },
];

/**
 * Words longer than two words: the guest's stock-out pills, which keep their
 * cause inside the pill (founder, 2026-10-06), and the countdown to a booked
 * morning (kept, founder, 2026-10-06).
 */
export const LONGER_ALLOWED: readonly PillAllowance[] = [
  {
    words: [
      'Sold out · {count} items',
      'Épuisé · {count} articles',
      'Sold out · Removed',
      'Épuisé · Retiré',
      'Sold out · {count} removed',
      'Épuisé · {count} en moins',
      'Sold out · Replaced',
      'Épuisé · Remplacé',
    ],
    reason:
      "The guest: what a counter's stock-out did to the order, its cause inside the pill (stockOut; founder, 2026-10-06: three or four words are fine where the reason must stay inside the pill).",
  },
  {
    words: ['In {count} days', 'Dans {count} jours'],
    reason:
      "The guest's orders list: how far off a booked morning is (guestOrderDue; founder, 2026-10-06: keep the countdown).",
  },
  {
    words: ['Paiement en attente', 'En attente de la boulangerie'],
    reason:
      "Partner's bakery orders in French (orderSummary): the approved Payment pending and Awaiting bakery, in the reader's language - French needs more words for them (#2004).",
  },
];
